import { toDate } from "@/utils/dateUtil"
import { replaceHtmlTagsForEditor } from "@/utils/helpers"

import { Base } from "./Base"
import { Comment } from "./Comment"
import { StructuredDescription } from "./EditorData"
import { ChangeItemByUser, FeContext, ItemStatus } from "./Item"
import { User } from "./User"

import type { ServerSendIdeaActivityNotification } from "./common"
import type { EpicStatus } from "./Epic"
import type { MilestoneStatus } from "./Milestone"
import type { SprintPreview } from "./Sprint"

export type IdeaStatus = "OPEN" | "CONVERTED" | "OBSOLETE" | "DONE"
export type IdeaVoteType = "SUPPORTED" | "OPPOSED"
export type IdeaSize = "XS" | "S" | "M" | "L" | "XL"

export type IdeaMenuActionId = "convertRoadmapFeature" | "convertEpic" | "convertBacklog" | "delete"

export interface IdeaMenuAction {
  id: IdeaMenuActionId
  label: string
  divider?: boolean
}

export interface IdeaConvertedEpicMilestone {
  id: string
  title: string
  status: MilestoneStatus
  startDate: string
  endDate: string
  estimatedDelayInDays: number
}

export interface IdeaConvertedEpic {
  id: string
  name: string
  sequenceNumber: number
  status?: EpicStatus
  milestone?: IdeaConvertedEpicMilestone | null
}

export interface IdeaConvertedItem {
  id: string
  title: string
  sequenceNumber: number
  status?: ItemStatus
  sprint?: SprintPreview | null
}

export interface IdeaStatusChange {
  status: IdeaStatus
  description?: StructuredDescription | null
  initiator?: ChangeItemByUser | null
}

export interface IdeaPreviewSseConversion {
  convertedToEpicId?: string
  convertedToEpicSequenceNumber?: number
  convertedToEpicTitle?: string
  convertedToItemId?: string
  convertedToItemSequenceNumber?: number
  convertedToItemTitle?: string
}

export class Idea extends Base<Idea> {
  id = ""
  sequenceNumber = 0
  title = ""
  structuredDescription: StructuredDescription = new StructuredDescription()
  label = ""
  size: IdeaSize | null = null
  feContext: FeContext | null = null
  status: IdeaStatus = "OPEN"
  obsoleteByConvertedResource = false
  ready = false
  priority = 0
  convertedToEpic: IdeaConvertedEpic | null = null
  convertedToItem: IdeaConvertedItem | null = null
  convertedToFeatureId: string | null = null
  ownerId = ""
  createdByUser: ChangeItemByUser | null = null
  supporters: User[] = []
  supportedCount = 0
  opposedCount = 0
  pendingCount = 0
  myVoteType: IdeaVoteType | null = null
  following = false
  createdDate: Date = new Date()
  modifiedDate: Date = new Date()
  statusChangeComment: Comment | null = null

  constructor(props?: Partial<Idea>) {
    super()

    if (!props) return

    Object.keys(props).forEach(field => {
      if (props[field] === undefined || props[field] === null) return
      this[field] = props[field]
    })

    this.structuredDescription = new StructuredDescription(this.structuredDescription)
    this.supporters = this.supporters.map(supporter => User.create(supporter as User))
    this.createdDate = this.createdDate ? toDate(this.createdDate) : new Date()
    this.modifiedDate = this.modifiedDate ? toDate(this.modifiedDate) : new Date()
    this.statusChangeComment = this.statusChangeComment
      ? Comment.create(this.statusChangeComment)
      : null
  }

  static create(props?: Partial<Idea>): Idea {
    return new Idea(props)
  }

  static createDefault(): Idea {
    return new Idea()
  }

  get isConverted(): boolean {
    return Boolean(this.convertedToEpic || this.convertedToItem || this.convertedToFeatureId)
  }

  get ideaKey(): string {
    return `I-${this.sequenceNumber}`
  }

  get statusChangeReason(): string {
    return this.statusChangeComment?.structuredDescription?.html ?? ""
  }

  get statusChangeInitiator(): ChangeItemByUser {
    const owner = this.statusChangeComment?.owner
    return {
      id: owner?.id ?? "",
      firstName: owner?.firstName ?? "",
      lastName: owner?.lastName ?? "",
      picture: { pictureUrl: null }
    }
  }

  get statusChangeAttachmentsCount(): number {
    return this.statusChangeComment?.fileMetadata?.filter(file => !file.deleted).length ?? 0
  }

  get unsaveFields() {
    return ["title", "structuredDescription"] as (keyof Idea)[]
  }

  isModified(keys?: (keyof Idea)[], inverse?: boolean): boolean {
    let isModified = false
    if (keys?.includes("structuredDescription") && this.initialState) {
      const initial = JSON.parse(this.initialState)
      isModified =
        replaceHtmlTagsForEditor(this.structuredDescription?.html ?? "") !==
        replaceHtmlTagsForEditor(initial.structuredDescription?.html ?? "")
      keys = keys.filter(key => key !== "structuredDescription")
    }
    if (!isModified) {
      isModified = super.isModified(keys, inverse)
    }
    return isModified
  }

  getJsonObj() {
    return {
      title: this.title,
      structuredDescription: this.structuredDescription,
      label: this.label,
      size: this.size,
      feContext: this.feContext
    }
  }
}

/** Nullable fields of the backend's full Idea preview (IdeaPreviewSseDto). */
const nullableIdeaPreviewFields: (keyof Idea)[] = [
  "label",
  "size",
  "structuredDescription",
  "priority",
  "ownerId",
  "convertedToFeatureId",
  "createdByUser",
  "statusChangeComment"
]

function resetIdeaField<K extends keyof Idea>(target: Idea, defaults: Idea, field: K) {
  target[field] = defaults[field]
}

function resolveConvertedEpic(
  current: Idea | undefined,
  preview: IdeaPreviewSseConversion
): IdeaConvertedEpic | null {
  if (!preview.convertedToEpicId) return null
  const known =
    current?.convertedToEpic?.id === preview.convertedToEpicId ? current.convertedToEpic : null
  return {
    ...known,
    id: preview.convertedToEpicId,
    name: preview.convertedToEpicTitle ?? known?.name ?? "",
    sequenceNumber: preview.convertedToEpicSequenceNumber ?? known?.sequenceNumber ?? 0
  }
}

function resolveConvertedItem(
  current: Idea | undefined,
  preview: IdeaPreviewSseConversion
): IdeaConvertedItem | null {
  if (!preview.convertedToItemId) return null
  const known =
    current?.convertedToItem?.id === preview.convertedToItemId ? current.convertedToItem : null
  return {
    ...known,
    id: preview.convertedToItemId,
    title: preview.convertedToItemTitle ?? known?.title ?? "",
    sequenceNumber: preview.convertedToItemSequenceNumber ?? known?.sequenceNumber ?? 0
  }
}

/**
 * The project-wide preview has no per-user vote. The supporters list shows a support; when the
 * current user voted in another tab, the change of the opposed count shows an oppose or a removed vote.
 */
function resolveMyVoteType(
  current: Idea | undefined,
  activity: ServerSendIdeaActivityNotification,
  currentUserId: string | undefined
): IdeaVoteType | null {
  const previous = current?.myVoteType ?? null
  const supporters = activity.idea.supporters
  if (!current || !currentUserId || !supporters) return previous
  if (supporters.some(user => user.id === currentUserId)) return "SUPPORTED"

  if (activity.event?.initiator?.id === currentUserId) {
    const opposedChange =
      (activity.idea.opposedCount ?? current.opposedCount) - current.opposedCount
    if (opposedChange > 0) return "OPPOSED"
    if (opposedChange < 0) return null
  }
  return previous === "SUPPORTED" ? null : previous
}

/**
 * Merges a project-wide Idea preview without overwriting the current user's follow state.
 * The preview is complete, but its JSON omits null values, so a missing nullable field was cleared.
 */
export function applyIdeaActivitySse(
  current: Idea | undefined,
  activity: ServerSendIdeaActivityNotification,
  currentUserId?: string
): Idea {
  // Post-commit events can arrive after a newer REST response or SSE event.
  if (
    current &&
    activity.idea.modifiedDate &&
    toDate(activity.idea.modifiedDate).getTime() < current.modifiedDate.getTime()
  )
    return current

  const {
    convertedToEpicId,
    convertedToEpicSequenceNumber,
    convertedToEpicTitle,
    convertedToItemId,
    convertedToItemSequenceNumber,
    convertedToItemTitle,
    ...preview
  } = activity.idea
  const conversion: IdeaPreviewSseConversion = {
    convertedToEpicId,
    convertedToEpicSequenceNumber,
    convertedToEpicTitle,
    convertedToItemId,
    convertedToItemSequenceNumber,
    convertedToItemTitle
  }

  const next = Idea.create({
    ...(current ?? {}),
    ...preview,
    // The full objects carry the sprint and milestone; the flat fields cover one the server could not load.
    convertedToEpic: preview.convertedToEpic ?? resolveConvertedEpic(current, conversion),
    convertedToItem: preview.convertedToItem ?? resolveConvertedItem(current, conversion),
    myVoteType: resolveMyVoteType(current, activity, currentUserId),
    following: current?.following ?? false
  })

  const defaults = Idea.createDefault()
  for (const field of nullableIdeaPreviewFields) {
    if (!(field in activity.idea)) resetIdeaField(next, defaults, field)
  }

  return next
}
